import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { AlertTriangle, ClipboardList, Phone, MapPin, Clock, MessageCircle } from 'lucide-react';
import RemindersWidget from '../RemindersWidget';
import SalesPerformanceCard from './SalesPerformanceCard';
import AdminCommissionsOverview from '../admin/AdminCommissionsOverview';
import { PermissionLevel } from '../../../types/permissions';
import { DashboardData } from '../../../services/dashboardService';
import { 
  LeadDto,
  leadsApi,
  LeadFilterDto 
} from '../../../services/leadsService';
import LeadHandlingModal from '../../LeadHandlingModal';

interface SalesRepDashboardProps {
  dashboardData: DashboardData;
  onDataUpdate: () => void;
}

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TabContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-light);
  overflow: hidden;
`;

const TabHeader = styled.div`
  display: flex;
  border-bottom: 2px solid var(--border-color);
  background: var(--surface-light);
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 16px 24px;
  background: ${props => props.$active ? 'var(--surface-color)' : 'transparent'};
  border: none;
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-secondary)'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${props => props.$active ? 'var(--surface-color)' : 'var(--hover-background)'};
  }

  ${props => props.$active && `
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--primary-color);
    }
  `}
`;

const TabBadge = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;

  &::before {
    content: '(';
  }

  &::after {
    content: ')';
  }
`;

const TabContent = styled.div`
  padding: 24px;
  min-height: 400px;
`;

const LeadsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface LeadCardProps {
  urgency?: 'red' | 'orange' | 'green';
}

const LeadCard = styled.div<LeadCardProps>`
  background: white;
  border: 1px solid var(--border-color);
  border-right: 3px solid ${props => {
    switch (props.urgency) {
      case 'red': return '#ef4444';
      case 'orange': return '#f59e0b';
      case 'green': return '#22c55e';
      default: return '#94a3b8';
    }
  }};
  border-radius: var(--border-radius-md);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: var(--shadow-medium);
    transform: translateY(-2px);
  }
`;

const LeadInfo = styled.div`
  flex: 1;
`;

const LeadName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
`;

const LeadDetails = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LeadActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover { background: transparent; color: var(--primary-color); }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover { background: var(--hover-background); }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: var(--background-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: var(--text-secondary);
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--error-color);
`;

const ErrorIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #dc262610;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #dc2626;
  filter: saturate(0.7) brightness(0.9);
`;

const ErrorText = styled.p`
  font-size: 16px;
  margin: 0 0 16px 0;
`;

const RetryButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--primary-light); color: var(--primary-color);
  }
`;

type TabType = string; // Dynamic status IDs as tab types

const SalesRepDashboard: React.FC<SalesRepDashboardProps> = ({ dashboardData, onDataUpdate }) => {
  const [activeTab, setActiveTab] = useState<TabType>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leads, setLeads] = useState<LeadDto[]>([]);
  const [selectedLead, setSelectedLead] = useState<LeadDto | null>(null);
  const [showHandlingModal, setShowHandlingModal] = useState(false);

  // Initialize activeTab with first lead status when data becomes available
  useEffect(() => {
    const leadStatuses = dashboardData.salesRepData?.leadStatuses;
    if (!activeTab && leadStatuses && leadStatuses.length > 0) {
      setActiveTab(leadStatuses[0].statusId.toString());
    }
  }, [dashboardData.salesRepData?.leadStatuses, activeTab]);

  const loadLeadsForTab = useCallback(async () => {
    if (!activeTab) return; // Don't load if no tab selected

    setLoading(true);
    setError(null);
    try {
      let filter: LeadFilterDto = {
        page: 1,
        pageSize: 50
      };

      // Filter by the selected status ID
      const statusId = parseInt(activeTab);
      if (!isNaN(statusId)) {
        filter.statusIds = [statusId];
      }

      const result = await leadsApi.getAll(filter);
      setLeads(result.leads);
    } catch (error) {
      console.error('Error loading leads:', error);
      setError('שגיאה בטעינת הלידים. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab) {
      loadLeadsForTab();
    }
  }, [activeTab, loadLeadsForTab]);

  // Refresh data when dashboard data updates
  useEffect(() => {
    if (activeTab) {
      loadLeadsForTab();
    }
  }, [dashboardData, activeTab, loadLeadsForTab]);

  const getLeadUrgency = (lead: LeadDto): 'red' | 'orange' | 'green' | undefined => {
    if (!lead.lastUpdatedAt) return 'red';
    
    const lastUpdate = new Date(lead.lastUpdatedAt);
    const now = new Date();
    const daysSinceUpdate = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysSinceUpdate > 3) return 'red';
    if (daysSinceUpdate > 1) return 'orange';
    return 'green';
  };

  const formatLastUpdate = (date?: string): string => {
    if (!date) return 'לא עודכן';
    
    const lastUpdate = new Date(date);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'עודכן עכשיו';
    if (diffInHours < 24) return `עודכן לפני ${diffInHours} שעות`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `עודכן לפני ${diffInDays} ימים`;
  };

  const handleLeadClick = (lead: LeadDto) => {
    setSelectedLead(lead);
    setShowHandlingModal(true);
  };

  const getPrimaryPhone = (lead: LeadDto): string => {
    return lead.contacts?.find(c => c.isPrimary)?.number
      ?? lead.contacts?.[0]?.number
      ?? '';
  };

  const handleCallClick = (lead: LeadDto, e: React.MouseEvent) => {
    e.stopPropagation();
    const phone = getPrimaryPhone(lead);
    if (phone) window.location.href = `tel:${phone}`;
  };

  const handleWhatsAppClick = (lead: LeadDto, e: React.MouseEvent) => {
    e.stopPropagation();
    const phone = getPrimaryPhone(lead);
    if (phone) window.open(`https://wa.me/972${phone.replace(/^0/, '')}`, '_blank');
  };

  const getEmptyStateText = (tab: TabType): string => {
    const statusId = parseInt(tab);
    if (!isNaN(statusId) && dashboardData.salesRepData?.leadStatuses) {
      const status = dashboardData.salesRepData.leadStatuses.find(s => s.statusId === statusId);
      if (status) {
        return `אין לידים בסטטוס ${status.statusName}`;
      }
    }

    return 'אין לידים';
  };

  // תצוגת צוות — מפקח מלא (isSupervisor) או מפקח חלקי (יש teamOverview אך isSupervisor=false)
  const isTeamView = dashboardData.userInfo.isSupervisor || !!dashboardData.salesRepData?.teamOverview;

  return (
    <DashboardContainer>
      {/* Reminders Widget - at the top like System Admin */}
      <RemindersWidget
        reminders={{
          today: dashboardData.remindersSummary.todayReminders,
          overdue: dashboardData.remindersSummary.overdueReminders,
          upcoming: dashboardData.remindersSummary.upcomingReminders
        }}
        counts={{
          todayCount: dashboardData.remindersSummary.todayCount,
          overdueCount: dashboardData.remindersSummary.overdueCount,
          upcomingCount: dashboardData.remindersSummary.upcomingCount
        }}
        onReminderUpdate={onDataUpdate}
      />

      {/* Performance Overview */}
      {dashboardData.salesRepData && (
        <SalesPerformanceCard
          performance={dashboardData.salesRepData.performance}
          teamPerformance={dashboardData.salesRepData.teamOverview?.teamPerformance}
          isSupervisor={isTeamView}
        />
      )}

      {/* עמלות — סיכום סופי + פילוח לפי לקוח (רק לנציג מכירות; מפקח מלא רואה את כל הנציגים) */}
      {dashboardData.userInfo.permissionLevel === PermissionLevel.SalesRep &&
        dashboardData.salesRepData?.commissionsOverview && (
          <AdminCommissionsOverview
            data={dashboardData.salesRepData.commissionsOverview}
            title={isTeamView ? 'עמלות נציגים' : 'העמלות שלי'}
            showRepNames={isTeamView}
          />
        )}

      {/* Tabbed Content - Lead Statuses */}
      <TabContainer>
        <TabHeader>
          {/* Dynamic tabs based on lead statuses */}
          {dashboardData.salesRepData?.leadStatuses.map((status) => (
            <Tab
              key={status.statusId}
              $active={activeTab === status.statusId.toString()}
              onClick={() => setActiveTab(status.statusId.toString())}
            >
              {status.statusName}
              {isTeamView && ' (צוות)'}
              <TabBadge>
                {isTeamView ? status.teamCount : status.count}
              </TabBadge>
            </Tab>
          ))}
        </TabHeader>

        <TabContent>
          {activeTab && (
            <>
              {loading ? (
                <LoadingState>טוען לידים...</LoadingState>
              ) : error ? (
                <ErrorState>
                  <ErrorIcon>
                    <AlertTriangle size={24} />
                  </ErrorIcon>
                  <ErrorText>{error}</ErrorText>
                  <RetryButton onClick={loadLeadsForTab}>נסה שוב</RetryButton>
                </ErrorState>
              ) : leads.length === 0 ? (
                <EmptyState>
                  <EmptyIcon>
                    <ClipboardList size={24} />
                  </EmptyIcon>
                  <EmptyText>
                    {getEmptyStateText(activeTab)}
                  </EmptyText>
                </EmptyState>
              ) : (
                <LeadsList>
                  {leads.map(lead => (
                    <LeadCard 
                      key={lead.id} 
                      urgency={getLeadUrgency(lead)}
                      onClick={() => handleLeadClick(lead)}
                    >
                      <LeadInfo>
                        <LeadName>{lead.fullName}</LeadName>
                        <LeadDetails>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Phone size={12} /> {getPrimaryPhone(lead)}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><MapPin size={12} /> {lead.leadSourceName}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Clock size={12} /> {formatLastUpdate(lead.lastUpdatedAt)}</span>
                        </LeadDetails>
                      </LeadInfo>
                      <LeadActions>
                        <ActionButton onClick={(e) => handleCallClick(lead, e)} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Phone size={14} /> התקשר
                        </ActionButton>
                        <ActionButton onClick={(e) => handleWhatsAppClick(lead, e)} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <MessageCircle size={14} /> וואטסאפ
                        </ActionButton>
                        <ActionButton variant="primary" onClick={() => handleLeadClick(lead)}>
                          טפל
                        </ActionButton>
                      </LeadActions>
                    </LeadCard>
                  ))}
                </LeadsList>
              )}
            </>
          )}
        </TabContent>
      </TabContainer>

      {/* Lead Handling Modal */}
      {selectedLead && (
        <LeadHandlingModal
          isOpen={showHandlingModal}
          onClose={() => {
            setShowHandlingModal(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onLeadUpdated={() => {
            loadLeadsForTab();
            onDataUpdate();
          }}
        />
      )}
    </DashboardContainer>
  );
};

export default SalesRepDashboard;